/* ==========================================================================
   <milo-configurator> — Size / Colour selector.
   Size, Material (fixed to Chenille) and Colour are all real Shopify options,
   so the buy box resolves a real variant for the chosen Size × Colour and the
   hero is that variant's image (Shopify CDN). Reads #milo-variants + #milo-display.
   Pure resolution helpers are exposed as statics for unit testing.
   ========================================================================== */
(function () {
  'use strict';

  var FIXED_MATERIAL = 'Chenille';

  /* ---- Pure helpers (unit-tested in test/configurator.test.js) ----------- */
  // Resolve the purchasable variant for a Size + Colour. Material is fixed to
  // Chenille, so only Chenille variants are eligible — never resolve another
  // material even if one is ever re-added to the product (that would sell the
  // wrong fabric under a "Chenille" label). Colour/material matched
  // case-insensitively; prefer an available variant, else the first; null when
  // the combination doesn't exist.
  function variantFor(variants, size, colour) {
    if (!Array.isArray(variants)) return null;
    var c = String(colour).toLowerCase();
    var mat = FIXED_MATERIAL.toLowerCase();
    var matches = variants.filter(function (v) {
      return v.size === size
        && String(v.colour).toLowerCase() === c
        && String(v.material).toLowerCase() === mat;
    });
    return matches.find(function (v) { return v.available !== false; }) || matches[0] || null;
  }
  function sizesInOrder(variants) {
    var seen = [];
    (variants || []).forEach(function (v) { if (!seen.includes(v.size)) seen.push(v.size); });
    return seen;
  }
  function formatDimensions(meta) {
    if (!meta) return '';
    var values = [meta.length_cm, meta.depth_cm, meta.height_cm, meta.seat_height_cm];
    if (values.some(function (value) { return typeof value !== 'number' || !Number.isFinite(value); })) return '';
    return 'L ' + meta.length_cm + ' × D ' + meta.depth_cm + ' × H ' + meta.height_cm
      + ' cm · Seating height ' + meta.seat_height_cm + ' cm';
  }
  function displayColour(colour) {
    return /^(?:blue|indigo)$/i.test(String(colour)) ? 'Indigo' : String(colour || '');
  }

  // Browser-only: the custom element. Guarded so Node can import the pure helpers.
  if (typeof HTMLElement !== 'undefined') {
  class MiloConfigurator extends HTMLElement {
    connectedCallback() {
      this.variants = this._json('milo-variants') || [];
      this.display = this._json('milo-display') || { sizes: {}, materials: {}, colours: {}, currency: 'S$' };
      this.isDemo = this.display.demo === true || this.variants.some((v) => v.demo);
      this.currency = this.display.currency || 'S$';

      this.state = {
        size: this._checked('cfg-size') || (sizesInOrder(this.variants)[0] || null),
        colour: this._checked('cfg-colour') || this.display.default_colour || null,
        material: FIXED_MATERIAL,
      };

      this._bind();
      const initialize = () => this.update({ emitImage: false });
      if (document.readyState === 'loading') {
        document.addEventListener('milo:ready', initialize, { once: true });
      } else {
        initialize();
      }

      // Demo preview (no product connected): flag that checkout is disabled.
      if (this.isDemo) {
        const note = this.querySelector('[data-demo-note]');
        if (note) note.hidden = false;
      }
    }

    _json(id) {
      const el = document.getElementById(id);
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { console.warn('[milo:cfg] bad JSON', id, e); return null; }
    }
    _checked(name) {
      const el = this.querySelector(`input[name="${name}"]:checked`);
      return el ? el.value : null;
    }

    _bind() {
      this.addEventListener('change', (e) => {
        if (e.target.name === 'cfg-size') { this.state.size = e.target.value; this.update(); }
        if (e.target.name === 'cfg-colour') { this.state.colour = e.target.value; this.update(); }
      });
      // Fabric details toggle
      const toggle = this.querySelector('[data-fabric-toggle]');
      if (toggle) toggle.addEventListener('click', () => {
        const card = this.querySelector('[data-fabric-card]');
        if (!card) return;
        const open = !card.hidden;
        card.hidden = open;
        toggle.setAttribute('aria-expanded', String(!open));
        toggle.textContent = open ? 'View fabric details' : 'Hide fabric details';
      });
    }

    _paintSizePrices() {
      this.querySelectorAll('[data-size-price]').forEach((el) => {
        const v = variantFor(this.variants, el.dataset.sizePrice, this.state.colour);
        const price = v ? v.price : null;
        el.textContent = price == null ? '' : 'from ' + window.MiloMoney(price, this.currency);
      });
    }

    update(options) {
      options = options || {};
      const { size, colour } = this.state;
      const colourLabel = displayColour(colour);
      const variant = variantFor(this.variants, size, colour);
      const matMeta = this.display.materials[FIXED_MATERIAL] || {};

      // Repaint the size "from" prices for the current colour (kept fresh in
      // case pricing ever becomes colour-dependent; today it's colour-neutral).
      this._paintSizePrices();

      // Size meta line
      const sizeMeta = this.display.sizes[size] || {};
      const metaEl = this.querySelector('[data-size-meta]');
      if (metaEl) metaEl.textContent = [sizeMeta.seats, formatDimensions(sizeMeta)].filter(Boolean).join(' · ');

      // Material (fixed) name + fabric card
      const matNameEl = this.querySelector('[data-material-name]');
      if (matNameEl) matNameEl.textContent = matMeta.label || FIXED_MATERIAL;
      this._paintFabricCard(matMeta);

      // Colour name (legend)
      const colMeta = (this.display.colours || {})[colour] || {};
      const colNameEl = this.querySelector('[data-colour-name]');
      if (colNameEl) colNameEl.textContent = colMeta.label || colourLabel;

      // Resolve variant → id, availability, hero image
      const available = !!variant && variant.available !== false && !this.isDemo;
      const idInput = this.querySelector('[data-variant-id]');
      if (idInput) idInput.value = variant ? variant.id : '';

      // Hero = the resolved variant's image; fall back to the colour thumbnail
      // so the hero still reacts in demo mode (no product) or if a variant is
      // ever missing its image.
      const img = (variant && variant.image) || colMeta.image;
      if (img && options.emitImage !== false) {
        this.dispatchEvent(new CustomEvent('milo:image-change', {
          bubbles: true,
          detail: { src: img, alt: ['Milo', colourLabel, size, 'modular sofa'].filter(Boolean).join(' ') },
        }));
      }

      this.dispatchEvent(new CustomEvent('milo:variant-change', {
        bubbles: true,
        detail: {
          variant,
          size,
          colour: colourLabel,
          colourValue: colour,
          material: FIXED_MATERIAL,
          price: variant ? variant.price : null,
          available,
          purchasable: available,
          isDemo: this.isDemo,
          currency: this.currency,
          label: [size, colourLabel, FIXED_MATERIAL].filter(Boolean).join(' · '),
          dimensions: sizeMeta,
          dimensionsLabel: formatDimensions(sizeMeta),
        },
      }));
    }

    _paintFabricCard(matMeta) {
      const card = this.querySelector('[data-fabric-card]');
      if (!card) return;
      const set = (sel, val) => { const el = card.querySelector(sel); if (el) el.textContent = val || ''; };
      set('[data-fc-name]', matMeta.label);
      set('[data-fc-composition]', matMeta.composition);
      set('[data-fc-care]', matMeta.care);
      set('[data-fc-blurb]', matMeta.blurb);
      const tag = card.querySelector('[data-fc-tag]');
      if (tag) { tag.textContent = matMeta.tag || ''; tag.hidden = !matMeta.tag; }
    }
  }

  MiloConfigurator.variantFor = variantFor;
  MiloConfigurator.sizesInOrder = sizesInOrder;
  MiloConfigurator.formatDimensions = formatDimensions;
  MiloConfigurator.displayColour = displayColour;

  if (typeof window !== 'undefined' && 'customElements' in window && !customElements.get('milo-configurator')) {
    customElements.define('milo-configurator', MiloConfigurator);
  }
  } // end browser-only guard

  // Export for Node-based unit tests
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { variantFor, sizesInOrder, formatDimensions, displayColour };
  }
})();
